import { Input, Radio, Tooltip } from "antd";
import type { TableColumnType } from "antd";
import { MdDelete, MdWarning } from "react-icons/md";
import InsumoSelect from "../../../OrdemServico/ModalLocaisRecursos/InsumoSelect";
import AlvoSelect from "./AlvoSelect";
import ComponenteSelect from "./ComponenteSelect";
import OperacaoTag from "./OperacaoTag";
import { Dispatch, SetStateAction } from "react";
import type { InsumoSelectPayload } from "../../../OrdemServico/consultarEstoque/types";

const { Group } = Radio;

function getColumnsLocais(
  handleChangeLocal: (talhaoId: string, key: string, value: string) => void,
  isEdit?: boolean,
  handleDeleteLocal?: (talhaoId: string) => void,
): TableColumnType<any>[] {
  const columns: TableColumnType<any>[] = [
    {
      title: "Talhão",
      dataIndex: "codigo",
      key: "codigo",
      width: 60,
      align: "center",
      render: (value: string) => <span>{value ?? "-"}</span>,
    },
    {
      title: "Operações Selecionadas",
      dataIndex: "planoOperacionalOperacaoTalhao",
      key: "planoOperacionalOperacaoTalhao",
      width: 200,
      align: "center",
      render: (value: any[]) => <OperacaoTag value={value} />,
    },
    {
      title: "Ýrea Total (ha)",
      dataIndex: "areaPlantadaHa",
      key: "areaPlantadaHa",
      width: 60,
      align: "center",
      render: (value: number) => {
        return <span>{value ? Number(value).toFixed(2) : 0}</span>
      },
    },
    {
      title: "Tipo",
      dataIndex: "tipo",
      key: "tipo",
      width: 60,
      align: "center",
      render: (value: string, record: any) => (
        <Group
          size="large"
          value={value}
          onChange={e => handleChangeLocal(record.talhaoId ?? record.talhaoIdExterno, "tipo", e.target.value)}
        >
          <Radio value="porcentagem">%</Radio>
          <Radio value="area">ha</Radio>
        </Group>
      ),
    },
    {
      title: "Valor a Executar",
      dataIndex: "valorExecutar",
      key: "valorExecutar",
      width: 60,
      align: "center",
      render: (value: string, record: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Input
            size="large"
            placeholder="Valor"
            value={value}
            onChange={(e) => {
              const value = e.target.value;

              if (!/^\d*[.,]?\d*$/.test(value)) return;

              const normalized = value.replace(",", ".");

              handleChangeLocal(record.talhaoId ?? record.talhaoIdExterno, "valorExecutar", normalized);
            }}
            onBlur={(e) => {
              const value = e.target.value;
              if (!value) return;

              const normalized = value.replace(",", ".");
              const parsed = parseFloat(normalized);

              if (!isNaN(parsed)) {
                const max = record.tipo === "porcentagem" ? 100 : record.areaPlantadaHa;
                const limited = Math.min(parsed, max);
                handleChangeLocal(record.talhaoId ?? record.talhaoIdExterno, "valorExecutar", limited.toString());
              }
            }}
            style={{ borderRadius: 10 }}
          />
          <span style={{ fontSize: 16 }}>{record.tipo === "porcentagem" ? "%" : "ha"}</span>
        </div>
      ),
    },
    {
      title: "Ýrea Efetiva (ha)",
      dataIndex: "areaEfetiva",
      key: "areaEfetiva",
      width: 60,
      align: "center",
      render: (value: number) => <p style={{ fontWeight: "bold", color: "#4caf50" }}>{value ? Number(value).toFixed(2) : 0}</p>,
    },
  ];

  if (isEdit) {
    columns.push({
      title: "Ações",
      dataIndex: "talhaoId",
      key: "acoes",
      width: 40,
      align: "center",
      render: (talhaoId: string, record: any) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Tooltip title={record?.apontamento ? "Apontado no PIMs" : undefined}>
            <MdDelete
              size={18}
              color={record?.apontamento ? "#ccc" : "#f00"}
              style={{ cursor: record.apontamento ? "not-allowed" : "pointer" }}
              onClick={() => {
                if (!record?.apontamento) {
                  handleDeleteLocal?.(talhaoId);
                }
              }}
            />
          </Tooltip>
        </div>
      ),
    });
  }

  return columns;
}

function getColumnsRecursos(
  recursos: any[],
  handleDeleteRecurso: (index: number) => void,
  handleChangeRecurso: (index: number, key: string, value: string | string[] | InsumoSelectPayload) => void,
  setSearchComponent: Dispatch<SetStateAction<Record<number, string>>>,
  searchComponent: Record<number, string>,
  refDose: React.RefObject<any>,
  ocupacaoId: string,
  isEdit: boolean = false,
  previsaoLinhas: any[],
  unidadeIdExterno?: string,
): TableColumnType<any>[] {
  return [
    {
      title: "Componente",
      dataIndex: "componenteIdExterno",
      key: "componenteIdExterno",
      align: "center",
      render: (value: string, record: any, index: number) => (
        <ComponenteSelect
          id={value}
          recursos={recursos}
          onChange={(idExterno) => handleChangeRecurso(index, "componenteIdExterno", idExterno)}
          unidadeIdExterno={unidadeIdExterno}
          searchComponent={searchComponent}
          index={index}
          disabled={isEdit && record.isDeleted}
          onSearch={(searchText) => {
            setSearchComponent((prev) => ({
              ...prev,
              [index]: searchText,
            }));
          }}
        />
      ),
    },
    {
      title: "Insumo",
      dataIndex: "insumoIdExterno",
      key: "insumoIdExterno",
      align: "center",
      render: (value: string, record: any, index: number) => (
        <InsumoSelect
          id={record.id}
          insumoId={value}
          disabled={isEdit && record.isDeleted}
          idExternoUnidade={unidadeIdExterno}
          idExternoComponente={record?.componenteIdExterno}
          onChange={(payload) => {
            handleChangeRecurso(index, "insumo", payload);
            refDose.current?.focus();
          }}
        />
      ),
    },
    {
      title: "Dose",
      dataIndex: "quantidade",
      key: "quantidade",
      width: 120,
      align: "center",
      render: (value: number, record: any, index: number) => (
        <Input
          type="number"
          size="large"
          placeholder="Dose"
          ref={refDose}
          value={value}
          onChange={e => handleChangeRecurso(index, "quantidade", e.target.value)}
          style={{ borderRadius: 10, textAlign: "center" }}
        />
      ),
    },
    {
      title: "Unidade",
      dataIndex: "unidade",
      key: "unidade",
      width: 80,
      align: "center",
      render: (value: string) => <span>{value ?? "-"}</span>,
    },
    {
      title: "% Utilização",
      dataIndex: "porcentagemUtilizacao",
      key: "porcentagemUtilizacao",
      width: 140,
      align: "center",
      render: (value: number, record: any, index: number) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Input
            type="number"
            size="large"
            placeholder="Utilização"
            value={value}
            disabled={isEdit && record.isDeleted}
            onChange={e => handleChangeRecurso(index, "porcentagemUtilizacao", e.target.value)}
            style={{ borderRadius: 10, textAlign: "center" }}
          />
          <span style={{ fontSize: 16 }}>%</span>
        </div>
      ),
    },
    {
      title: "Qtd. Produção",
      dataIndex: "quantidadeProducao",
      key: "quantidadeProducao",
      width: 180,
      align: "center",
      render: (value: number, record: any, index: number) => (
        <Input
          type="number"
          size="large"
          value={value}
          disabled={isEdit && record.isDeleted}
          onChange={e => handleChangeRecurso(index, "quantidadeProducao", e.target.value)}
          placeholder="Qtd. Produção"
          style={{ borderRadius: 10, textAlign: "center" }}
        />
      ),
    },
    {
      title: "Alvo",
      dataIndex: "alvoIdsExternos",
      key: "alvoIdsExternos",
      width: 200,
      align: "center",
      render: (value: string, record: any, index: number) => (
        <AlvoSelect
          id={value}
          ocupacaoId={ocupacaoId}
          disabled={isEdit && record.isDeleted}
          onChange={(idExterno) => handleChangeRecurso(index, "alvoIdsExternos", idExterno)}
        />
      ),
    },
    {
      title: "Ações",
      dataIndex: "id",
      key: "id",
      width: 120,
      align: "center",
      render: (_value: number, record: any, index: number) => {
        const linha = previsaoLinhas.find(item => item.insumoId === record.insumoId);
        const hasAlerta = linha?.previsaoEstoque <= 0;
        return (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 4 }}>
            <Tooltip title={record?.apontamento ? "Apontado no PIMs" : undefined}>
              <MdDelete
                size={18}
                color={record?.apontamento ? "#ccc" : "#f00"}
                style={{ cursor: record.apontamento ? "not-allowed" : "pointer" }}
                onClick={() => {
                  if (!record?.apontamento) {
                    handleDeleteRecurso(index);
                  }
                }}
              />
            </Tooltip>
            {hasAlerta && (
              <Tooltip title="Previsão de Falta de Insumo no Estoque">
                <span style={{ display: "inline-flex", cursor: "default" }}>
                  <MdWarning size={20} color="#e53935" />
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];
}

export { getColumnsLocais, getColumnsRecursos }
